<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from '#imports'
import { useAuthStore } from '~/stores/auth'
import { ColorName } from '~/models/Content/Color'
import useOidc from '~/composables/useOidc'
import { useAppColor } from '~/composables/useAppColor'
import useSelfServiceApi from '~/api/selfServiceApi'
import Button from '~/components/ui/Button.vue'
import AuCallout from '~/components/ui/AuCallout.vue'
import InputError from '~/components/register/InputError.vue'
import FlowHeader from '~/components/FlowHeader.vue'

const { t } = useI18n()
const route = useRoute()
const { isAuthenticated } = storeToRefs(useAuthStore())
const { activateUser } = useSelfServiceApi()
const { buildLocalUrl } = useOidc()
useAppColor(ColorName.Gray5)

const viewMode = ref<'activate' | 'success'>('activate')
const inputType = ref<'password' | 'text'>('password')
const password = ref('')
const error = ref('')
const blurred = ref(false)
const isLoading = ref(false)

const username = computed(() => route.query?.username as string || '')
const token = computed(() => route.query?.code as string || '')

const validPassword = computed(() => password.value.length >= 8
  && /^(?=[^A-ZÅØÆ\s]*[A-ZÅØÆ])(?=[^\d\s]*\d)\S*$/i.test(password.value))

const changeInput = computed(() => inputType.value === 'password'
  ? t('register.activate.inputText')
  : t('register.activate.inputPassword'))

const hasParams = computed(() => username.value && token.value)
const canActivate = computed(() => validPassword.value && hasParams.value)

function toggleInput() {
  inputType.value = inputType.value === 'password' ? 'text' : 'password'
}

async function activatePassword() {
  if (!canActivate.value) {
    return
  }

  error.value = ''
  isLoading.value = true
  try {
    await activateUser({
      username: username.value,
      password: password.value,
      token: token.value
    })
    viewMode.value = 'success'
  } catch (e) {
    error.value = t('register.error.activate')
    throw e
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <FlowHeader v-if="!isAuthenticated" />
  <section class="max-w-screen-au relative m-auto mt-8 px-4 sm:px-8">
    <div class="grid grid-cols-1 place-items-center">
      <div>
        <h1
          class="mb-8 text-center text-4xl font-semibold"
          v-text="t('register.activate.title')"
        />
        <p
          v-if="username"
          class="mb-8 text-center"
          v-text="t('register.activate.info')"
        />
        <AuCallout
          v-if="!hasParams"
          class="mb-4"
          variant="error"
          :title="t('register.error.missingParams')"
        />
        <AuCallout
          v-if="error"
          class="mb-4"
          variant="error"
          :title="error"
        />
        <AuCallout
          v-if="viewMode === 'success'"
          variant="success"
          :title="t('register.activate.success')"
        />

        <div v-if="viewMode === 'activate'">
          <label>
            <span
              class="mb-2 inline-block"
              v-text="t('register.input.password')"
            />
            <input
              v-model="password"
              :type="inputType"
              :disabled="!hasParams"
              class="border-gray-10 block w-full rounded-xl border bg-white p-2 focus-visible:ring disabled:cursor-not-allowed"
              @blur="blurred = true"
              @input="blurred = false"
            >
          </label>
          <div class="flex justify-between">
            <InputError
              v-if="!validPassword && blurred && password.length > 0"
              class="order-last"
              :text="t('register.invalid.password')"
            />
            <p class="text-gray-40 pt-2 text-right">
              <button
                class="underline focus-visible:ring"
                @click.prevent="toggleInput"
                v-text="changeInput"
              />
            </p>
          </div>
        </div>

        <div class="mt-4 flex justify-end gap-4">
          <Button
            href="/"
            variant="outline"
            size="lg"
            :disabled="isLoading"
          >
            {{ viewMode === 'success' ? t('back') : t('cancel') }}
          </Button>
          <Button
            v-if="viewMode === 'activate'"
            size="lg"
            :disabled="!canActivate"
            @click.prevent="activatePassword"
          >
            {{ t('register.form.submit') }}
          </Button>
          <Button
            v-if="viewMode === 'success'"
            :href="buildLocalUrl"
            size="lg"
          >
            {{ t('login.title') }}
          </Button>
        </div>
      </div>
    </div>
  </section>
</template>
